import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { useUser } from '../../context/UserContext';
import { canAccessAdmin } from '../../utils/roleUtils';
import agencyAdminService from '../../services/agencyAdminService';
import { 
  DollarSign, TrendingUp, BarChart3, PieChart, Download, 
  Calendar, Filter, Search, AlertCircle, CheckCircle, Clock,
  RefreshCw, Eye, Settings, CreditCard, Banknote, Percent,
  ArrowUpRight, ArrowDownRight, Users, Target, Zap, Star
} from 'lucide-react';
import '../../styles/modern-design-system.css';
import '../../styles/agency-admin.css';

const FinancialManagement = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    pendingCommissions: 0,
    paidCommissions: 0,
    avgCommissionRate: 0,
    totalAgents: 0,
    // Enhanced metrics
    quarterlyGrowth: 0,
    commissionEfficiency: 0,
    avgPayoutTime: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
    commissionStructure: 'tiered'
  });
  const [ setRevenueData] = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [payoutData, setPayoutData] = useState([]);
  const [commissionStructure ] = useState({
    type: 'tiered',
    tiers: [
      { min: 0, max: 10000, rate: 8 },
      { min: 10000, max: 50000, rate: 10 },
      { min: 50000, max: 100000, rate: 12 },
      { min: 100000, max: null, rate: 15 }
    ]
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  // Check if user has agency admin access
  const hasAgencyAdminAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  useEffect(() => {
    if (!hasAgencyAdminAccess()) {
      navigate('/agent/dashboard');
      return;
    }
    fetchFinancialData();
  }, [user, navigate]);

  const fetchFinancialData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Try to fetch from API first
      try {
        const [commissionData, financialReports] = await Promise.all([
          agencyAdminService.getCommissionData(user?.agencyId, timeRange),
          agencyAdminService.getFinancialReports(user?.agencyId, 'overview', timeRange)
        ]);
        
        // Safely set metrics with fallbacks
        if (commissionData?.metrics) {
          setMetrics({
            totalRevenue: commissionData.metrics.totalRevenue || 0,
            monthlyRevenue: commissionData.metrics.monthlyRevenue || 0,
            pendingCommissions: commissionData.metrics.pendingCommissions || 0,
            paidCommissions: commissionData.metrics.paidCommissions || 0,
            avgCommissionRate: commissionData.metrics.avgCommissionRate || 0,
            totalAgents: commissionData.metrics.totalAgents || 0,
            quarterlyGrowth: commissionData.metrics.quarterlyGrowth || 0,
            commissionEfficiency: commissionData.metrics.commissionEfficiency || 0,
            avgPayoutTime: commissionData.metrics.avgPayoutTime || 0,
            totalPayouts: commissionData.metrics.totalPayouts || 0,
            pendingPayouts: commissionData.metrics.pendingPayouts || 0,
            commissionStructure: commissionData.metrics.commissionStructure || 'tiered'
          });
        }
        
        if (commissionData?.commissions) {
          setCommissionData(commissionData.commissions);
        }
        
        if (commissionData?.payouts) {
          setPayoutData(commissionData.payouts);
        }
        
        if (financialReports?.revenueData) {
          setRevenueData(financialReports.revenueData);
        }
      } catch (apiError) {
        console.log('API not available, using mock data:', apiError);
        
        // Enhanced mock data
        setMetrics({
          totalRevenue: 125000,
          monthlyRevenue: 15000,
          pendingCommissions: 12500,
          paidCommissions: 87500,
          avgCommissionRate: 12.5,
          totalAgents: 8,
          quarterlyGrowth: 15.2,
          commissionEfficiency: 94.5,
          avgPayoutTime: 3.2,
          totalPayouts: 87500,
          pendingPayouts: 12500,
          commissionStructure: 'tiered'
        });

        setRevenueData([
          { month: 'Jan', revenue: 12000, commissions: 1500, payouts: 1200 },
          { month: 'Feb', revenue: 13500, commissions: 1687, payouts: 1350 },
          { month: 'Mar', revenue: 14200, commissions: 1775, payouts: 1420 },
          { month: 'Apr', revenue: 15800, commissions: 1975, payouts: 1580 },
          { month: 'May', revenue: 16500, commissions: 2062, payouts: 1650 },
          { month: 'Jun', revenue: 15000, commissions: 1875, payouts: 1500 }
        ]);

        setCommissionData([
          { 
            id: 1,
            agent: 'Sarah Johnson', 
            amount: 2500, 
            status: 'paid', 
            date: '2024-01-15',
            tripValue: 25000,
            commissionRate: 10,
            payoutDate: '2024-01-18',
            method: 'bank_transfer'
          },
          { 
            id: 2,
            agent: 'Mike Chen', 
            amount: 1800, 
            status: 'pending', 
            date: '2024-01-14',
            tripValue: 18000,
            commissionRate: 10,
            payoutDate: null,
            method: 'bank_transfer'
          },
          { 
            id: 3,
            agent: 'Emily Rodriguez', 
            amount: 1200, 
            status: 'paid', 
            date: '2024-01-12',
            tripValue: 12000,
            commissionRate: 10,
            payoutDate: '2024-01-15',
            method: 'paypal'
          },
          { 
            id: 4,
            agent: 'David Kim', 
            amount: 2200, 
            status: 'pending', 
            date: '2024-01-10',
            tripValue: 22000,
            commissionRate: 10,
            payoutDate: null,
            method: 'bank_transfer'
          },
          { 
            id: 5,
            agent: 'Lisa Wang', 
            amount: 1600, 
            status: 'paid', 
            date: '2024-01-08',
            tripValue: 16000,
            commissionRate: 10,
            payoutDate: '2024-01-11',
            method: 'check'
          }
        ]);

        setPayoutData([
          {
            id: 1,
            agent: 'Sarah Johnson',
            amount: 2500,
            status: 'completed',
            scheduledDate: '2024-01-18',
            completedDate: '2024-01-18',
            method: 'bank_transfer',
            reference: 'TXN-001234'
          },
          {
            id: 2,
            agent: 'Mike Chen',
            amount: 1800,
            status: 'scheduled',
            scheduledDate: '2024-01-20',
            completedDate: null,
            method: 'bank_transfer',
            reference: null
          },
          {
            id: 3,
            agent: 'Emily Rodriguez',
            amount: 1200,
            status: 'completed',
            scheduledDate: '2024-01-15',
            completedDate: '2024-01-15',
            method: 'paypal',
            reference: 'PP-567890'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
      // Ensure fallback data is set even on unexpected errors
      setMetrics({
        totalRevenue: 0,
        monthlyRevenue: 0,
        pendingCommissions: 0,
        paidCommissions: 0,
        avgCommissionRate: 0,
        totalAgents: 0,
        quarterlyGrowth: 0,
        commissionEfficiency: 0,
        avgPayoutTime: 0,
        totalPayouts: 0,
        pendingPayouts: 0,
        commissionStructure: 'tiered'
      });
      setRevenueData([]);
      setCommissionData([]);
      setPayoutData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (!hasAgencyAdminAccess()) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Financial Management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
          <p className="text-gray-600">Loading financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <motion.main 
        className="flex-1 p-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Financial Management</h1>
                <p className="mt-2 text-gray-600">Monitor revenue, commissions, and financial performance</p>
              </div>
              <div className="flex items-center space-x-4">
                {/* Time Range Selector */}
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <select
                    value={timeRange}
                    onChange={(e) => {
                      setTimeRange(e.target.value);
                      fetchFinancialData();
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="1y">Last year</option>
                  </select>
                </div>
                
                {/* Refresh Button */}
                <button
                  onClick={() => fetchFinancialData(true)}
                  disabled={refreshing}
                  className="flex items-center px-3 py-2 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>

                {/* Export Button */}
                <button className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700">
                  <Download className="mr-2 w-4 h-4" />
                  Export Report
                </button>
              </div>
            </div>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px space-x-8">
                {[
                  { id: 'overview', label: 'Overview', icon: BarChart3 },
                  { id: 'commissions', label: 'Commissions', icon: Percent },
                  { id: 'payouts', label: 'Payouts', icon: CreditCard },
                  { id: 'structure', label: 'Commission Structure', icon: Settings }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      selectedTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="mr-2 w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Financial Metrics */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: 'Total Revenue',
                value: `$${(metrics?.totalRevenue || 0).toLocaleString()}`,
                subtitle: 'All time revenue',
                icon: DollarSign,
                color: 'green',
                change: '+12.5%',
                changeType: 'positive'
              },
              {
                title: 'Monthly Revenue',
                value: `$${(metrics?.monthlyRevenue || 0).toLocaleString()}`,
                subtitle: 'This month',
                icon: TrendingUp,
                color: 'blue',
                change: '+8.2%',
                changeType: 'positive'
              },
              {
                title: 'Pending Commissions',
                value: `$${(metrics?.pendingCommissions || 0).toLocaleString()}`,
                subtitle: 'Awaiting payout',
                icon: Clock,
                color: 'yellow',
                change: 'Pending',
                changeType: 'neutral'
              },
              {
                title: 'Paid Commissions',
                value: `$${(metrics?.paidCommissions || 0).toLocaleString()}`,
                subtitle: 'Completed payouts',
                icon: CheckCircle,
                color: 'purple',
                change: 'Paid',
                changeType: 'positive'
              }
            ].map((metric) => (
              <motion.div
                key={metric.title}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -2 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="agency-metric-card agency-fade-in"
              >
                <div className="flex justify-between items-center mb-4">
                  <div className={`agency-metric-icon bg-${metric.color}-100`}>
                    <metric.icon className={`w-6 h-6 text-${metric.color}-600`} />
                  </div>
                  <div className={`text-sm font-medium ${
                    metric.changeType === 'positive' ? 'revenue-trend-up' :
                    metric.changeType === 'negative' ? 'revenue-trend-down' :
                    'text-gray-600'
                  }`}>
                    {metric.changeType === 'positive' ? (
                      <ArrowUpRight className="mr-1 w-4 h-4" />
                    ) : metric.changeType === 'negative' ? (
                      <ArrowDownRight className="mr-1 w-4 h-4" />
                    ) : null}
                    {metric.change}
                  </div>
                </div>
                <div>
                  <h3 className="agency-metric-value">{metric.value}</h3>
                  <p className="agency-metric-label">{metric.title}</p>
                  <p className="agency-metric-subtitle">{metric.subtitle}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Tab Content */}
          {selectedTab === 'overview' && (
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Charts Section */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Revenue Chart */}
                <motion.div 
                  variants={itemVariants}
                  className="financial-card agency-slide-up"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Revenue Trends</h3>
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-400 rounded-lg hover:text-gray-600 hover:bg-gray-100">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 rounded-lg hover:text-gray-600 hover:bg-gray-100">
                        <Download className="w-4 h-4" />
                      </button>
                      <BarChart3 className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  <div className="financial-chart">
                    <div className="text-center">
                      <BarChart3 className="mx-auto mb-2 w-12 h-12 text-gray-300" />
                      <p className="text-gray-500">Revenue chart will be implemented here</p>
                      <p className="mt-1 text-sm text-gray-400">Interactive charts coming soon</p>
                    </div>
                  </div>
                </motion.div>

                {/* Commission Breakdown */}
                <motion.div 
                  variants={itemVariants}
                  className="financial-card agency-slide-up"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Commission Breakdown</h3>
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-400 rounded-lg hover:text-gray-600 hover:bg-gray-100">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 rounded-lg hover:text-gray-600 hover:bg-gray-100">
                        <Download className="w-4 h-4" />
                      </button>
                      <PieChart className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  <div className="financial-chart">
                    <div className="text-center">
                      <PieChart className="mx-auto mb-2 w-12 h-12 text-gray-300" />
                      <p className="text-gray-500">Commission breakdown chart will be implemented here</p>
                      <p className="mt-1 text-sm text-gray-400">Interactive charts coming soon</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Commissions Tab */}
          {selectedTab === 'commissions' && (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Commission Details</h3>
                  <div className="flex gap-2">
                    <button className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Search className="mr-1 w-4 h-4" />
                      Search
                    </button>
                    <button className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Calendar className="mr-1 w-4 h-4" />
                      Date Range
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Agent</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Trip Value</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Rate</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Commission</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Status</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Date</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(commissionData || []).map((commission, index) => (
                        <motion.tr
                          key={commission?.id ?? `commission-${index}`}
                          className="border-b border-gray-100 transition-colors hover:bg-gray-50"
                          whileHover={{ backgroundColor: '#f9fafb' }}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{commission?.agent || 'N/A'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">${(commission?.tripValue || 0).toLocaleString()}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-600">{commission?.commissionRate || 0}%</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">${(commission?.amount || 0).toLocaleString()}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              commission?.status === 'paid' 
                                ? 'commission-status-paid' 
                                : 'commission-status-pending'
                            }`}>
                              {commission?.status === 'paid' ? (
                                <CheckCircle className="mr-1 w-3 h-3" />
                              ) : (
                                <Clock className="mr-1 w-3 h-3" />
                              )}
                              {commission?.status || 'pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {commission?.date ? new Date(commission.date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                              View Details
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* Payouts Tab */}
          {selectedTab === 'payouts' && (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Payout Management</h3>
                  <button className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                    <CreditCard className="mr-2 w-4 h-4" />
                    Process Payouts
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Agent</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Amount</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Method</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Status</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Scheduled</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Reference</th>
                        <th className="px-4 py-3 font-medium text-left text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(payoutData || []).map((payout, index) => (
                        <motion.tr
                          key={payout?.id ?? `payout-${index}`}
                          className="border-b border-gray-100 transition-colors hover:bg-gray-50"
                          whileHover={{ backgroundColor: '#f9fafb' }}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{payout?.agent || 'N/A'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">${(payout?.amount || 0).toLocaleString()}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-600 capitalize">{(payout?.method || '').replace('_', ' ')}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              payout?.status === 'completed' 
                                ? 'bg-green-100 text-green-800' 
                                : payout?.status === 'scheduled'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {payout?.status === 'completed' ? (
                                <CheckCircle className="mr-1 w-3 h-3" />
                              ) : (
                                <Clock className="mr-1 w-3 h-3" />
                              )}
                              {payout?.status || 'pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {payout?.scheduledDate ? new Date(payout.scheduledDate).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {payout?.reference || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                              View Details
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* Commission Structure Tab */}
          {selectedTab === 'structure' && (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Commission Structure</h3>
                  <button className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                    <Settings className="mr-2 w-4 h-4" />
                    Edit Structure
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="mb-2 font-medium text-gray-900">Current Structure: Tiered Commission</h4>
                    <p className="text-sm text-gray-600">Commission rates increase based on total revenue generated</p>
                  </div>

                  <div className="space-y-3">
                    {commissionStructure.tiers.map((tier) => (
                      <motion.div
                        key={tier.min}
                        className="flex justify-between items-center p-4 rounded-lg border border-gray-200"
                        whileHover={{ backgroundColor: '#f9fafb' }}
                      >
                        <div>
                          <div className="font-medium text-gray-900">
                            ${tier.min.toLocaleString()} - {tier.max ? `$${tier.max.toLocaleString()}` : '∞'}
                          </div>
                          <div className="text-sm text-gray-600">Revenue range</div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">{tier.rate}%</div>
                          <div className="text-sm text-gray-600">Commission rate</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
      </motion.main>
    </div>
  );
};

export default FinancialManagement;
